import { Dialog, DialogContent, DialogHeading, ListItem } from 'omakase';
import { KeyboardEvent, KeyboardEventHandler } from 'react';
import { MenuList } from '@material-ui/core';
import { PLAYLIST_DEFAULTS } from 'selectors/patronSelectors';
import { consumer } from '@tock/coretypes';
import { useActions } from '@tock/shared/utils/connect';
import confirm from 'utils/confirm';

interface PlaylistOptionsDialogProps {
  isOpen: boolean;
  onClose(): void;
  onEditListDetails(): void;
  onDeleteListDone?(): void;
  onShareList(): void;
  playlist?: consumer.Playlist;
}

const PlaylistOptionsDialog: React.FunctionComponent<PlaylistOptionsDialogProps> = ({
  isOpen,
  onClose,
  onEditListDetails,
  onDeleteListDone,
  onShareList,
  playlist,
}) => {
  const { actions } = useActions();

  if (!playlist) {
    return null;
  }

  const isPublic = playlist.isPublic;
  const isDefaultList = PLAYLIST_DEFAULTS.includes(playlist.name);

  const handleDeletePlaylist = () => {
    if (!onDeleteListDone) {
      return;
    }
    actions.patron.upsertPlaylist({ ...playlist, isArchived: true });
    onDeleteListDone();
  };

  const handleChangePlaylistPermissions = () => {
    const permissionType = isPublic ? 'PRIVATE' : 'PUBLIC';
    actions.patron.upsertPlaylist({ ...playlist, isPublic: permissionType === 'PUBLIC' });
    onClose();
  };

  const handleDelete = () => {
    onClose();
    return confirm
      .show('Are you sure you want to delete this saved list?')
      .then(handleDeletePlaylist)
      .catch();
  };

  const getHandleEnter = (callback: Function): KeyboardEventHandler => {
    return (event: KeyboardEvent<HTMLLIElement>) => {
      if (event.key === 'Enter') {
        callback();
      }
    };
  };

  return (
    <Dialog
      variant="sheet"
      fullWidth
      open={isOpen}
      onClose={onClose}
      aria-label="Saved list options"
    >
      <DialogHeading onClose={onClose}>List options</DialogHeading>
      <DialogContent disablePadding>
        <MenuList autoFocusItem>
          <ListItem
            variant="basic"
            size="large"
            weight="medium"
            onKeyPress={getHandleEnter(onEditListDetails)}
            onClick={onEditListDetails}
          >
            Edit list details
          </ListItem>
          {isPublic && (
            <ListItem
              variant="basic"
              size="large"
              weight="medium"
              onKeyPress={getHandleEnter(onShareList)}
              onClick={onShareList}
            >
              Share list
            </ListItem>
          )}
          <ListItem
            variant="basic"
            size="large"
            weight="medium"
            onKeyPress={getHandleEnter(handleChangePlaylistPermissions)}
            onClick={handleChangePlaylistPermissions}
          >
            {isPublic ? 'Make private' : 'Make public'}
          </ListItem>
          {!isDefaultList && (
            <ListItem
              variant="basic"
              size="large"
              weight="medium"
              onKeyPress={getHandleEnter(handleDelete)}
              onClick={handleDelete}
            >
              Delete list
            </ListItem>
          )}
        </MenuList>
      </DialogContent>
    </Dialog>
  );
};

export default PlaylistOptionsDialog;
